import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ConceptualCategoryCard } from '../components/conceptual-problems/ConceptualCategoryCard';
import { AddCategoryDialog } from '../components/conceptual-problems/AddCategoryDialog';
import { conceptualProblemsApi } from '../api/conceptualProblemsApi';
import type { ConceptualCategory } from '../types/conceptualProblems';

/**
 * The way in to the conceptual problems: every topic, with how many problems it holds
 * (feature 029).
 *
 * <p><b>The list is data, not code.</b> Eleven categories exist today and more are expected, so
 * nothing here enumerates or hard-codes them — a category added to the table appears on the next
 * load with no change to the website. The grid is column-count-driven rather than laid out per
 * category, so it reads correctly at two entries or at thirty, and stacks to one column on a
 * phone.</p>
 */
export function ConceptualCategoriesPage() {
  usePageTitle('Conceptual Problems — Hawkes Bridge');

  const [categories, setCategories] = useState<ConceptualCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Creating a category (2026-09-10 amendment, FR-034).
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addNotice, setAddNotice] = useState('');

  const load = useCallback(() => {
    setError(null);
    setCategories(null);
    conceptualProblemsApi
      .getCategories()
      .then(response => setCategories(response.categories))
      .catch(() => setError('Unable to load the categories. Please try again.'));
  }, []);

  useEffect(load, [load]);

  const handleAdd = useCallback(async (name: string) => {
    setAdding(true);
    setAddError(null);

    try {
      const created = await conceptualProblemsApi.createCategory(name);

      // Appended rather than re-sorted or refetched: the list is ordered by category id, and a
      // new category has the highest id there is, so the end IS its place. It arrives with a
      // count of zero, which the card shows — an empty topic is listed, not hidden (FR-003).
      setCategories((previous) => (previous ? [...previous, created] : [created]));
      setShowAdd(false);
      setAddNotice(`Added the category ${created.name}.`);
    } catch (err: unknown) {
      // The server's own sentence where it has one: a duplicate NAMES the existing category, and
      // that is the thing the editor needs in order to decide what to do next (US5 scenario 2).
      // This is also how the concurrent-create race arrives — two editors, one name, one winner.
      const message = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      setAddError(message ?? 'Unable to add that category. Please try again.');
    } finally {
      setAdding(false);
    }
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <header className="flex items-center flex-wrap gap-3 mb-2">
        <h1 className="text-2xl font-bold text-ink">Conceptual Problems</h1>
        <button
          type="button"
          onClick={() => { setAddError(null); setShowAdd(true); }}
          className="ml-auto px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
          data-testid="conceptual-add-category-button"
        >
          Add category
        </button>
        <Link
          to="/data-editing"
          className="px-3 py-1.5 text-sm font-medium text-ink-soft hover:text-ink border border-line-strong rounded-md"
        >
          Back to Data Editing
        </Link>
      </header>
      <p className="text-sm text-ink-soft mb-6 max-w-2xl">
        Choose a topic to read its problems one at a time — the auction, the question and the
        answer as they stand today.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700" role="alert">
          <p className="mb-3">{error}</p>
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-cream text-ink border border-brass hover:bg-paper"
          >
            Try again
          </button>
        </div>
      )}

      {!error && !categories && (
        <div className="flex items-center justify-center min-h-[40vh]">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!error && categories && categories.length === 0 && (
        <div className="bg-cream border border-line rounded-lg p-6 text-ink-soft" data-testid="conceptual-categories-empty">
          There are no conceptual categories yet.
        </div>
      )}

      {!error && categories && categories.length > 0 && (
        <div
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="conceptual-categories"
        >
          {categories.map(category => (
            <ConceptualCategoryCard key={category.id ?? 'uncategorised'} category={category} />
          ))}
        </div>
      )}

      {showAdd && (
        <AddCategoryDialog
          isSaving={adding}
          error={addError}
          onSave={handleAdd}
          onCancel={() => { setShowAdd(false); setAddError(null); }}
        />
      )}

      {/* The new card appearing in the grid is the sighted confirmation; this is the same fact for
          a screen reader, which would otherwise be told only that a dialog closed. */}
      <div className="sr-only" role="status" aria-live="polite" data-testid="conceptual-add-notice">
        {addNotice}
      </div>
    </div>
  );
}
